import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FileText, File, Globe, Layout } from 'lucide-react';
import { Button } from '../ui/Button';
interface ExportDocumentationModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function ExportDocumentationModal({
  isOpen,
  onClose
}: ExportDocumentationModalProps) {
  const [format, setFormat] = useState('markdown');
  const [audience, setAudience] = useState('technical');
  if (!isOpen) return null;
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <motion.div initial={{
      scale: 0.95,
      y: 20
    }} animate={{
      scale: 1,
      y: 0
    }} className="w-full max-w-4xl bg-surface-900 border border-surface-600 rounded-xl shadow-2xl overflow-hidden flex">
        {/* Left Panel - Options */}
        <div className="w-1/2 p-6 border-r border-surface-600">
          <h2 className="text-xl font-semibold text-text-primary mb-6">
            Export Documentation
          </h2>

          <div className="space-y-6">
            {/* Format */}
            <div>
              <label className="text-sm font-medium text-text-secondary uppercase tracking-wider mb-3 block">
                Format
              </label>
              <div className="grid grid-cols-2 gap-3">
                {[{
                id: 'markdown',
                label: 'Markdown',
                icon: FileText
              }, {
                id: 'pdf',
                label: 'PDF',
                icon: File
              }, {
                id: 'html',
                label: 'HTML',
                icon: Globe
              }, {
                id: 'notion',
                label: 'Notion',
                icon: Layout
              }].map(opt => <button key={opt.id} onClick={() => setFormat(opt.id)} className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${format === opt.id ? 'bg-primary-500/10 border-primary-500 text-primary-400' : 'bg-surface-800/50 border-surface-600 text-text-secondary hover:bg-surface-800'}`}>
                    <opt.icon className="w-4 h-4" />
                    <span className="text-sm font-medium">{opt.label}</span>
                  </button>)}
              </div>
            </div>

            {/* Audience */}
            <div>
              <label className="text-sm font-medium text-text-secondary uppercase tracking-wider mb-3 block">
                Audience
              </label>
              <div className="space-y-2">
                {[{
                id: 'technical',
                label: 'Technical Team',
                desc: 'Full detail, includes code'
              }, {
                id: 'stakeholders',
                label: 'Stakeholders',
                desc: 'Summary focus, decisions'
              }, {
                id: 'external',
                label: 'External',
                desc: 'Polished, minimal internal refs'
              }].map(opt => <button key={opt.id} onClick={() => setAudience(opt.id)} className={`w-full text-left p-3 rounded-lg border transition-all ${audience === opt.id ? 'bg-primary-500/10 border-primary-500' : 'bg-surface-800/50 border-surface-600 hover:bg-surface-800'}`}>
                    <div className={`text-sm font-medium ${audience === opt.id ? 'text-primary-400' : 'text-text-primary'}`}>
                      {opt.label}
                    </div>
                    <div className="text-xs text-text-muted">{opt.desc}</div>
                  </button>)}
              </div>
            </div>

            {/* Content */}
            <div>
              <label className="text-sm font-medium text-text-secondary uppercase tracking-wider mb-3 block">
                Content
              </label>
              <div className="space-y-2">
                {['Overview and objectives', 'Decisions made (with rationale)', 'Approaches tried', 'Final outputs and artifacts', 'Key code snippets'].map((item, i) => <label key={i} className="flex items-center gap-3 p-2 rounded hover:bg-surface-800/50 cursor-pointer">
                    <input type="checkbox" defaultChecked className="rounded border-surface-500 bg-surface-900 text-primary-500 focus:ring-primary-500" />
                    <span className="text-sm text-text-primary">{item}</span>
                  </label>)}
              </div>
            </div>
          </div>
        </div>

        {/* Right Panel - Preview */}
        <div className="w-1/2 bg-surface-900/50 flex flex-col">
          <div className="p-4 border-b border-surface-600 bg-surface-900">
            <h3 className="text-sm font-semibold text-text-primary">
              Preview Outline
            </h3>
          </div>
          <div className="flex-1 p-6 overflow-y-auto">
            <div className="space-y-4 opacity-60">
              <div className="h-8 bg-surface-800 rounded w-3/4" />
              <div className="space-y-2">
                <div className="h-4 bg-surface-800 rounded w-full" />
                <div className="h-4 bg-surface-800 rounded w-5/6" />
                <div className="h-4 bg-surface-800 rounded w-4/6" />
              </div>
              <div className="h-32 bg-surface-800 rounded w-full border border-surface-500" />
              <div className="space-y-2">
                <div className="h-4 bg-surface-800 rounded w-full" />
                <div className="h-4 bg-surface-800 rounded w-5/6" />
              </div>
            </div>
          </div>
          <div className="p-6 border-t border-surface-600 bg-surface-900 flex justify-end gap-3">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button className="bg-primary-600 hover:bg-blue-700 text-text-primary border-none">
              Export Document
            </Button>
          </div>
        </div>
      </motion.div>
    </motion.div>;
}